import React, { useState } from "react";
import { supabase } from "../../lib/supabaseClient";

const ImportChristies = () => {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Extraire le nom de la vente depuis l'URL Christie's
  function extractAuctionName(url) {
    try {
      // Ex: https://www.christies.com/en/auction/21st-century-evening-sale-featuring-works-30670/
      const match = url.match(/\/auction\/([^/]+)/);
      if (match) {
        // Convertir les tirets en espaces et capitaliser
        const slug = match[1].replace(/-\d+$/, ''); // Enlever le numéro final
        return slug
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      return "Vente Christie's";
    } catch (e) {
      return "Vente Christie's";
    }
  }

  const handleImport = async () => {
    if (!url.trim()) {
      alert("Merci d'indiquer l'URL de la vente Christie's !");
      return;
    }
    
    // Validation de l'URL
    if (!url.includes('christies.com')) {
      alert("L'URL doit être une page Christie's (christies.com)");
      return;
    }
    
    setLoading(true);
    setStatus("🔍 Analyse de la page en cours...");
    setResult(null);
 
    try {
      // Extraire le nom automatiquement
      const auctionName = extractAuctionName(url);
      
      console.log("🚀 Envoi vers Edge Function:", {
        auction_url: url,
        auction_name: auctionName
      });

      // ✅ Utilise la méthode native de Supabase
      const { data, error } = await supabase.functions.invoke('import_christies', {
        body: {
          auction_url: url,
          auction_name: auctionName, // ✅ Toujours fourni maintenant
          city: "New York",
          country: "États-Unis",
          date_start: new Date().toISOString().split('T')[0], // Date du jour par défaut
          house_name: "Christie's",
        }
      });

      if (error) {
        console.error("❌ Erreur Edge Function:", error);
        throw error;
      }
      
      console.log("✅ Résultat:", data);
      setResult(data);
      setStatus(`✅ ${data.inserted || 0} lots importés (${data.skipped || 0} ignorés)`);
    } catch (e) {
      console.error("❌ Erreur complète:", e);
      setStatus(`❌ Erreur: ${e.message}`);
      
      // Message d'aide selon le type d'erreur
      if (e.message.includes("400")) {
        setStatus("❌ Erreur 400: Vérifiez que l'URL est correcte et que la page contient des lots");
      } else if (e.message.includes("500")) {
        setStatus("❌ Erreur serveur: Réessayez dans quelques instants");
      } else if (e.message.includes("network") || e.message.includes("fetch")) {
        setStatus("❌ Erreur réseau: Vérifiez votre connexion internet");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
      <h2 className="text-xl font-semibold mb-3">
        🔨 Importer une vente Christie's
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Collez l'URL complète de la page de vente Christie's. L'import récupère automatiquement 
        les lots avec artiste, titre, numéro et estimation.
      </p>
      
      {/* Exemples d'URLs */}
      <details className="mb-4 text-xs text-gray-500">
        <summary className="cursor-pointer hover:text-gray-700">
          Voir des exemples d'URLs valides
        </summary>
        <div className="mt-2 space-y-1 pl-4">
          <div>✓ https://www.christies.com/en/auction/21st-century-evening-sale-30670/</div>
          <div>✓ https://www.christies.com/en/auction/post-war-contemporary-art-30671/</div>
          <div>✗ https://www.christies.com/ (page d'accueil - ne marche pas)</div>
        </div>
      </details>

      {/* Input URL */}
      <div className="space-y-3">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.christies.com/en/auction/..."
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          disabled={loading}
        />
        
        <button
          onClick={handleImport}
          disabled={loading || !url.trim()}
          className="w-full bg-black text-white px-5 py-3 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Import en cours...
            </span>
          ) : (
            "🚀 Lancer l'import"
          )}
        </button>
      </div>

      {/* Statut */}
      {status && (
        <div className={`mt-4 p-3 rounded-lg ${
          status.includes("✅") 
            ? "bg-green-50 text-green-800 border border-green-200" 
            : status.includes("❌") 
            ? "bg-red-50 text-red-800 border border-red-200"
            : "bg-blue-50 text-blue-800 border border-blue-200"
        }`}>
          {status}
        </div>
      )}

      {/* Résultats détaillés */}
      {result && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <span>📊</span>
            Résultat de l'import
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Maison</div>
              <div className="font-medium">ID: {result.house_id?.slice(0, 8)}...</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Vente</div>
              <div className="font-medium">ID: {result.sale_id?.slice(0, 8)}...</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Lots trouvés</div>
              <div className="font-medium text-blue-700">{result.lots_found || 0}</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Lots insérés</div>
              <div className="font-semibold text-green-700">{result.inserted || 0}</div>
            </div>
          </div>
          
          {result.skipped > 0 && (
            <div className="mt-3 text-xs text-gray-600 bg-white p-2 rounded">
              ℹ️ {result.skipped} lot(s) ignoré(s) (déjà présents en base)
            </div>
          )}
        </div>
      )}

      {/* Aide */}
      <details className="mt-4 text-xs text-gray-500">
        <summary className="cursor-pointer hover:text-gray-700">
          ❓ L'import ne fonctionne pas ?
        </summary>
        <div className="mt-2 space-y-2 pl-4">
          <div>• Vérifiez que l'URL est une page de vente complète (pas la page d'accueil)</div>
          <div>• La page doit contenir des lots avec leurs informations</div>
          <div>• Si l'erreur persiste, consultez les logs dans Supabase Dashboard</div>
          <div>• Christie's peut bloquer le scraping : réessayez plus tard</div>
        </div>
      </details>
    </div>
  );
};

export default ImportChristies;